/* //* Package imports */
import axios from "axios";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Cookies from "universal-cookie";
import * as yup from "yup";

/* //* Component imports */
import ErrorMessage from "../common/ErrorMessage";
import { header } from "./../common/cookieChecker";
import CountryCode from "../common/countryCode";

/* //* Service imports */
import {
  getCityPath,
  getCountriesPath,
  getPartnerLeadFormPath,
  getProperties,
  getRoomTypes,
  getUniversityPath,
} from "../common/urls";
import {
  authUserId,
  authUserUtmMedium,
  authUserUtmSource,
  range,
} from "../common/utils";

/* //* Style imports */
import "react-datepicker/dist/react-datepicker.css";

const cookies = new Cookies();

const GetInTouchSchema = yup.object().shape({
  name: yup
    .string()
    .required()
    .matches(/^[a-zA-Z ]+$/),
  phone: yup
    .string()
    .min(6)
    .max(15)
    .matches(/^[0-9]*$/)
    .required(),
  email: yup.string().email().required(),
  movein: yup.string().required(),
  country: yup.string().required(),
  city: yup.string().required(),
});

const formatDate = (date) => {
  let month = "" + (date.getMonth() + 1);
  let day = "" + date.getDate();
  let year = date.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
};

const DatePickerInput = ({ getter, handleChange, name }) => {
  const ExampleCustomInput = ({ value, onClick }) => (
    <Form.Group as={Col} controlid="Idname" className="pl-0">
      <Form.Label>Move In</Form.Label>
      <Form.Control
        id="movein"
        size="md"
        required
        type="text"
        placeholder="Pick move in date"
        name="movein"
        value={value}
        onClick={onClick}
      />
    </Form.Group>
  );

  return (
    <DatePicker
      dateFormat="yyyy-MM-dd"
      selected={getter ? new Date(getter) : null}
      onChange={(date) => handleChange(name, date)}
      customInput={<ExampleCustomInput />}
    />
  );
};

const EnquiryForm = () => {
  let isSubmitLocked = false;
  const [prevIndex, setIndex] = useState();
  const [prevVal, setInxVal] = useState();
  const [selectedOption, setSelectedOption] = useState("option1");
  const [selectedCity, setCity] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [universityOptions, setUniversityOptions] = useState([]);
  const [selectedProperty, setProperty] = useState("");
  const [propertyOptions, setPropertyOptions] = useState("");
  const [selectedRoomtype, setRoomtype] = useState("");
  const [roomtypeOptions, setRoomtypeOptions] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("£");

  const handleOptionChange = (changeEvent) => {
    setSelectedOption(changeEvent.target.value);
  };

  const fetchProperty = (city) => {
    axios.get(getProperties(city), header()).then(({ data }) => {
      let properties = data?.properties?.map((ele) => ({
        value: ele?.code,
        label: ele?.name + " (" + ele?.manager + ")",
        id: ele?.id,
      }));
      setPropertyOptions(properties);
    });
  };

  const fetchPropertyDetails = (city, property) => {
    axios.get(getRoomTypes(city, property), header()).then(({ data }) => {
      let room_type = data?.configs?.map((ele) => ({
        value: ele?.code,
        label: ele?.name,
      }));
      setRoomtypeOptions(room_type);
    });
  };

  const fetchCityData = (country) => {
    axios.get(getCityPath(country), header()).then(({ data }) => {
      let cities = data?.cities?.map((ele) => ({
        value: ele?.code,
        label: ele?.name,
      }));
      setCityOptions(cities);
    });
  };

  const fetchUniversity = (country) => {
    axios.get(getUniversityPath(country), header()).then(({ data }) => {
      let places = data?.places?.map((ele) => ({
        value: ele?.code,
        label: ele?.name,
      }));
      setUniversityOptions(places);
    });
  };

  const fetchCountryOptions = () => {
    axios
      .get(getCountriesPath, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(({ data }) => {
        const countries = data?.res?.map((country) => ({
          value: country?.code,
          label: country?.name,
          symbol: country?.symbol,
        }));
        setCountryOptions(countries);
      });
  };

  useEffect(() => {
    fetchCountryOptions();
  }, []);

  const setCC = (ele) => {
    if (prevIndex && ele.target.options) {
      ele.target.options[prevIndex].text = prevVal;
    }
    let selectText = ele.target.options[ele.target.selectedIndex].text;
    let dataVal =
      ele.target[ele.target.selectedIndex].getAttribute("data-countrycode");
    ele.target.options[ele.target.selectedIndex].text =
      dataVal + `(+${ele.target.value})`;
    if (prevIndex) {
      ele.target.options[prevIndex].text = prevVal;
    }
    setIndex(ele.target.selectedIndex);
    setInxVal(selectText);
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-center">
        <Formik
          initialValues={{
            budget: "",
            city: "",
            country: "",
            name: "",
            phone: "",
            university: "",
            email: "",
            movein: new Date(),
            source: authUserId,
            countryCode: "44",
            leaseDuration: "",
            utm_source: authUserUtmSource,
            utm_medium: authUserUtmMedium,
            remark: "",
            counsellor: "",
            property: "",
            room_type: "",
          }}
          validationSchema={GetInTouchSchema}
          onSubmit={(values, { setSubmitting, isSubmitting, resetForm }) => {
            values.city = selectedCity || "";
            values.country = selectedCountry;
            values.property = selectedProperty || "";
            values.room_type = selectedRoomtype || "";
            values.university = selectedUniversity || "";
            values.budget = values.budget ? parseInt(values.budget) : "";
            values.movein = values.movein
              ? formatDate(new Date(values.movein))
              : "";

            if (!isSubmitLocked) {
              isSubmitLocked = true;
              let formData = Object.assign({}, values);
              let auth_token = cookies.get("auth_token");
              let messageEl = document.getElementById("formMessage");
              messageEl.textContent = "";

              axios({
                method: "post",
                url: getPartnerLeadFormPath(),
                data: JSON.stringify(formData),
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${auth_token}`,
                },
              })
                .then(function (response) {
                  document
                    .getElementById("studentButton")
                    .removeAttribute("disabled", "disabled");
                  messageEl.textContent = "";
                  messageEl.setAttribute(
                    "class",
                    "fade alert alert-success show"
                  );
                  if (
                    messageEl.textContent == undefined ||
                    messageEl.textContent == ""
                  ) {
                    let textEl = document.createTextNode("Lead created.");
                    messageEl.appendChild(textEl);
                  } else {
                    document.getElementById("email").value = "";
                    messageEl.removeAttribute("class", "show");
                    messageEl.textContent = "";
                  }
                  resetForm();
                  setSelectedCountry("");
                  document.getElementById("partnerLeadForm").reset();
                  isSubmitLocked = false;
                  return response;
                })
                .catch(function (response) {
                  messageEl.textContent = "";
                  messageEl.setAttribute(
                    "class",
                    "fade alert alert-danger show"
                  );
                  let textEl = document.createTextNode(
                    "Please provide a valid phone number."
                  );
                  messageEl.appendChild(textEl);
                  isSubmitLocked = false;
                });
            }

            setTimeout(() => {
              setSubmitting(false);
            }, 500);
          }}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              dirty,
              isSubmitting,
              handleChange,
              handleBlur,
              handleSubmit,
              handleReset,
              setFieldValue,
              isInvalid,
            } = props;
            return (
              <Form id="partnerLeadForm" noValidate onSubmit={handleSubmit}>
                <Form.Row>
                  <Form.Group as={Col} md={6} controlid="Idname">
                    <Form.Label>
                      Full Name <span className="colorRed">*</span>
                    </Form.Label>
                    <Form.Control
                      id="name"
                      size="md"
                      required
                      type="text"
                      placeholder="Full Name"
                      name="name"
                      value={values?.name}
                      onChange={handleChange}
                      isInvalid={touched.name && errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter your Full Name.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} md={6}>
                    <Form.Label>
                      Phone Number <span className="colorRed">*</span>
                    </Form.Label>
                    <Form.Row>
                      <Col md={4} xs={4} sm={4} className="pr-0">
                        <Form.Control
                          size="md"
                          as="select"
                          className="CcClass"
                          onChange={(e) => {
                            values.countryCode = e.target.value;
                            setCC(e);
                          }}
                        >
                          <CountryCode />
                        </Form.Control>
                      </Col>

                      <Col md={8} xs={8} sm={8} className="pl-0">
                        <Form.Control
                          size="md"
                          type="text"
                          placeholder="Phone"
                          className="PhoneClass"
                          required
                          name="phone"
                          value={values?.phone}
                          onChange={handleChange}
                          isInvalid={touched.phone && errors.phone}
                        />
                      </Col>
                    </Form.Row>
                    <Form.Control
                      hidden
                      isInvalid={touched.phone && errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid phone.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Form.Row>

                <Form.Row>
                  <Form.Group as={Col} controlid="IdEmail">
                    <Form.Label>
                      Email Address <span className="colorRed">*</span>
                    </Form.Label>
                    <Form.Control
                      size="md"
                      type="email"
                      placeholder="Email"
                      required
                      name="email"
                      value={values?.email}
                      onChange={handleChange}
                      isInvalid={touched.email && errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid email address.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} className="pr-0" controlId="IdCountry">
                    <Form.Label>
                      Country <span className="colorRed">*</span>
                    </Form.Label>
                    <Select
                      options={countryOptions}
                      placeholder="Country"
                      onChange={(option) => {
                        fetchCityData(option.value);
                        fetchUniversity(option.value);
                        setSelectedCountry(option.value);
                        setCurrencySymbol(option.symbol);
                        setFieldValue("country", option.value);
                      }}
                      value={countryOptions.filter(
                        (option) => option.value === selectedCountry
                      )}
                    ></Select>
                    <ErrorMessage
                      condition={errors.country && touched.country}
                      message={errors.country}
                    ></ErrorMessage>
                  </Form.Group>
                </Form.Row>

                <Form.Row>
                  <Form.Group
                    as={Col}
                    xs={12}
                    md={6}
                    controlId="IdCity"
                    className="mt-3 mt-md-0"
                  >
                    <Form.Label>
                      City <span className="colorRed">*</span>
                    </Form.Label>
                    <Select
                      options={cityOptions}
                      placeholder="City"
                      onChange={(ele) => {
                        fetchProperty(ele.value);
                        setCity(ele.value);
                        setFieldValue("city", ele.value);
                      }}
                    ></Select>
                    <ErrorMessage
                      condition={errors.city}
                      message={errors.city}
                    ></ErrorMessage>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    className="pr-0"
                    controlId="IdUniversity"
                  >
                    <Form.Label>University</Form.Label>
                    <Select
                      options={universityOptions}
                      placeholder="University"
                      onChange={(ele) => {
                        setSelectedUniversity(ele.value);
                        setFieldValue("university", ele.value);
                      }}
                    />
                  </Form.Group>
                </Form.Row>

                <Form.Row>
                  <Col className="col pb-4 pt-2">
                    <Form.Row>
                      <Col xs={12} md={3} className="pl-4 mr-4 mb-2 mb-md-0">
                        <Form.Group controlId="formBasicCheckbox">
                          <Form.Check
                            className="form-check-input"
                            onChange={handleOptionChange}
                            type="radio"
                            value="option1"
                            checked={selectedOption === "option1"}
                            label="Don't Know Property"
                            style={{
                              width: 200,
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={3} className="pl-4 mb-2 mb-md-0">
                        <Form.Group controlId="formBasicCheckbox2">
                          <Form.Check
                            className="form-check-input"
                            onChange={handleOptionChange}
                            type="radio"
                            value="option2"
                            checked={selectedOption === "option2"}
                            label="Select Property"
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                  </Col>
                </Form.Row>

                {selectedOption == "option1" ? (
                  <Form.Row className="mt-4 mt-md-0">
                    <Form.Group as={Col}>
                      {/* <Form.Label>Move-in date? <span className="colorRed">*</span></Form.Label> */}
                      <DatePickerInput
                        name="movein"
                        getter={values?.movein}
                        handleChange={setFieldValue}
                      />
                      <Form.Control
                        hidden
                        isInvalid={touched.movein && errors.movein}
                      />
                      <Form.Control.Feedback type="invalid">
                        Field required
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} className="Budget">
                      <Form.Label>Budget</Form.Label>
                      <Form.Control
                        as="select"
                        onChange={(e) => {
                          values.budget = e.target.value
                            ? parseInt(e.target.value)
                            : "";
                        }}
                        isInvalid={touched.budget && errors.budget}
                      >
                        <option value="">Select budget</option>
                        {Array.from(range(5, 40)).map((value, ind) => {
                          let val = value * 10 + 50;
                          return (
                            <option
                              value={val}
                            >{`${currencySymbol} ${val} /week`}</option>
                          );
                        })}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        Please provide budget
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} controlid="IdLease">
                      <Form.Label>Lease</Form.Label>
                      <Form.Control
                        as="select"
                        onChange={(e) => {
                          values.leaseDuration = e.target.value;
                        }}
                        isInvalid={
                          touched.leaseDuration && errors.leaseDuration
                        }
                      >
                        <option value="">Select duration</option>
                        <option value="<=8">
                          Less than or equal to 8 weeks
                        </option>
                        <option value="12-24">12 - 24 weeks</option>
                        <option value="24-36">24 - 36 weeks</option>
                        <option value="44">44 weeks</option>
                        <option value="51">51 weeks</option>
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        Please provide lease duration
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Form.Row>
                ) : (
                  <>
                    <Row className="my-1">
                      <Form.Group
                        as={Col}
                        className="pr-0"
                        controlId="formGridEmail"
                      >
                        <Form.Label>
                          Property Name <span className="colorRed">*</span>
                        </Form.Label>
                        <Select
                          name="property_id"
                          placeholder="Property Name"
                          onChange={(ele) => {
                            fetchPropertyDetails(selectedCity, ele.value);
                            setProperty(ele.value);
                          }}
                          options={propertyOptions}
                          isMulti={false}
                          isSearchable
                        />
                        {roomtypeOptions.length != 0 && (
                          <Form.Group>
                            <a
                              href={`https://uniacco.com/${selectedCity}/${
                                selectedProperty.length != 0
                                  ? selectedProperty
                                  : ""
                              }?utm_source=${authUserUtmSource}&utm_medium=${authUserUtmMedium}`}
                              target="_blank"
                            >
                              Property Link
                            </a>
                          </Form.Group>
                        )}
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        className="pr-0"
                        controlId="formGridEmail"
                      >
                        <Form.Label>
                          Room Type <span className="colorRed">*</span>
                        </Form.Label>
                        <Select
                          name="room_type"
                          placeholder="Room type"
                          onChange={(ele) => {
                            setRoomtype(ele.value);
                          }}
                          options={roomtypeOptions}
                          isMulti={false}
                          isSearchable
                        />
                      </Form.Group>
                    </Row>
                  </>
                )}

                <Form.Row>
                  <Form.Group as={Col} md={6} controlid="IdRemark">
                    <Form.Label>Remark</Form.Label>
                    <Form.Control
                      id="remark"
                      size="md"
                      type="text"
                      placeholder="Remark"
                      name="remark"
                      value={values?.remark}
                      onChange={handleChange}
                      isInvalid={touched.remark && errors.remark}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter your remark.
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlid="IdCounsellor">
                    <Form.Label>Counsellor</Form.Label>
                    <Form.Control
                      id="counsellor"
                      size="md"
                      type="text"
                      placeholder="Counsellor"
                      name="counsellor"
                      value={values?.counsellor}
                      onChange={handleChange}
                      isInvalid={touched.counsellor && errors.counsellor}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter your counsellor.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} controlid="IdSubmit">
                    <Button
                      id="studentButton"
                      style={{ background: "#045e92" }}
                      type="submit"
                      disabled={isSubmitLocked}
                      className="submitButton shadow"
                      size="lg"
                      block
                    >
                      Create
                    </Button>
                  </Form.Group>
                </Form.Row>
                <div id="formMessage"></div>
                <div className="text-center"></div>
              </Form>
            );
          }}
        </Formik>
      </div>
      <style>{`

.type_suggestion {
  z-index:100 !important;
  padding-left:15px;
  padding-top:4px;
}

  .invalid-feedback-university {
    width: 100%;
    margin-top: 0.25rem;
    font-size: 80%;
    color: rgb(220, 53, 69);
}
    `}</style>
    </Container>
  );
};

export default EnquiryForm;
